<script setup>
import { computed, defineProps, watch } from "vue";
import { useAppLogo } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppLogo";
import { useAppSave } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppSave";
import { useAppStatus } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppStatus";
import { useStore } from "@/core/services/store";
import { useFeatures } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatures";
import { useMigration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMigration";
import { useArticleImport } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useArticleImport";
import { useMigrationIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMigrationIntegration";
import MigrateIntegrationsModal from "@/components/ExternalApps/SalesChannelManagementApp/components/MigrateIntegrationsModal.vue";
import { useHelp } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useHelp";
import i18n from "@/core/plugins/vue-i18n";
import HelpArea from "@/components/ExternalApps/SalesChannelManagementApp/components/HelpArea.vue";
import { useRoute, useRouter } from "vue-router/composables";
import {
  loadForms,
  resetValuesToSave
} from "@/composables/useSalesChannelManagementApp";
import SaveDialog from "@/components/ExternalApps/SalesChannelManagementApp/components/SaveDialog.vue";
import { ref } from "vue";
import DataOptions from "@/components/Tools/FormHelper/dataOptions";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import ChatBotButton from "@/components/ChatBot/components/ChatBotButton.vue";

defineProps({
  isDetailPage: {
    type: Boolean,
    default: () => false
  },
  showIntegrationOptions: {
    type: Boolean,
    default: () => false
  }
});

const store = useStore();
const { isAppInitialized } = useAppStatus();
const { logo } = useAppLogo();
const { save } = useAppSave();
const { selectedFeature } = useFeatures();
const { finishMigration, migrationStepFinished } = useMigration();
const { showArticleImportPrepareModal, isFinished, saveAction } =
  useArticleImport();
const {
  hasUnmigratedChannels,
  show,
  step,
  resetValues,
  migrationButtonText,
  migrationButtonIcon
} = useMigrationIntegration();
const { handbookLink, videoLink, getLinkForTypeAndArea } = useHelp();
const router = useRouter();
const route = useRoute();
const showSaveDialog = ref(false);
const { getIntegrationsWithPresets, selectedIntegrationLabel } =
  useIntegration();

const helpItems = computed(() => [
  {
    href: handbookLink.value,
    icon: "fa-book",
    label: i18n.t("salesChannelManagementApp.helpArea.handbook")
  },
  {
    href: videoLink.value,
    icon: "fa-play",
    label: i18n.t("salesChannelManagementApp.helpArea.video")
  }
]);
const helpItemsOverview = computed(() => [
  {
    href: getLinkForTypeAndArea("handbook", "listing", false),
    icon: "fa-book",
    label: i18n.t("salesChannelManagementApp.helpArea.handbook")
  },
  {
    href: getLinkForTypeAndArea("video", "listing", false),
    icon: "fa-play",
    label: i18n.t("salesChannelManagementApp.helpArea.video")
  }
]);

function onBackClicked() {
  if (Object.keys(store.getters.valuesToSave || {}).length > 0) {
    showSaveDialog.value = true;
    return;
  }

  router.push({ name: "salesChannelManagementApp" });
}

async function onSave(routeBack = false) {
  showSaveDialog.value = false;
  if (route.params.area === "settings") {
    DataOptions.clearCache(); // Clear the cache so that we don't use options from other integrations
    await loadForms();
  }
  await save(false);
  await getIntegrationsWithPresets();

  if (routeBack) {
    await router.push({ name: "salesChannelManagementApp" });
  }
}

function onSaveDialogBack() {
  showSaveDialog.value = false;
  resetValuesToSave();
  router.push({ name: "salesChannelManagementApp" }).then(() => {
    store.dispatch("resetSelectedIntegration");
  });
}

watch(
  () => show.value,
  () => {
    if (show.value) return;

    resetValues();
    step.value = 1;
  }
);
</script>

<template>
  <div class="d-flex justify-content-between align-items-center mb-4">
    <template v-if="isDetailPage">
      <div class="d-flex">
        <!--   START: BACK BUTTON   -->
        <button
          v-b-popover.hover="$t('salesChannelManagementApp.backToOverview')"
          class=""
          @click="onBackClicked"
        >
          <i class="fal fa-chevron-left" />
        </button>
        <!--   END: BACK BUTTON   -->
        <div v-if="isAppInitialized" class="h5 mb-0 ml-5 font-weight-bolder">
          {{ selectedIntegrationLabel }}
        </div>
      </div>

      <!--    START: ACTIONS & SAVE    -->
      <div
        v-if="isAppInitialized && selectedFeature !== 'journal'"
        class="actions align-self-end d-flex"
      >
        <HelpArea :items="helpItems"></HelpArea>

        <button
          v-if="selectedFeature === 'migration'"
          class="btn btn-primary mr-2"
          :disabled="
            !migrationStepFinished.export || !migrationStepFinished.import
          "
          @click="finishMigration"
        >
          {{ $t("salesChannelManagementApp.migration.finish") }}
        </button>

        <div v-if="selectedFeature === 'productSelection'" class="btn-group">
          <button
            :disabled="!isFinished"
            class="btn btn-primary"
            @click="saveAction()"
          >
            {{ $t("salesChannelManagementApp.useFilterForAllProducts") }}
          </button>
          <v-menu
            content-class="bg-white product-selection-filter-menu"
            bottom
            offset-y
          >
            <template #activator="{ on, attrs }">
              <button
                v-bind="attrs"
                type="button"
                class="btn btn-primary dropdown-toggle dropdown-toggle-split"
                v-on="on"
              ></button>
            </template>

            <perfect-scrollbar
              :options="{ wheelPropagation: false }"
              class="scroll h-100 max-height"
            >
              <ul class="navi navi-hover pa-0">
                <li
                  aria-haspopup="true"
                  data-menu-toggle="hover"
                  class="navi-item"
                >
                  <span
                    class="navi-link cursor-pointer"
                    @click="showArticleImportPrepareModal = true"
                  >
                    {{
                      $t(
                        "salesChannelManagementApp.useFilterForSelectedProducts"
                      )
                    }}
                  </span>
                </li>
              </ul>
            </perfect-scrollbar>
          </v-menu>
        </div>

        <button v-else class="btn btn-primary" @click="onSave(false)">
          {{ $t("general.save") }}
        </button>
        <ChatBotButton class="ml-2" />
      </div>
      <!--    END: ACTIONS & SAVE    -->
    </template>
    <template v-else>
      <!--   START: LOGO/ADD INTEGRATION   -->
      <div>
        <img :src="logo" alt="" class="shop-logo" />
      </div>
      <div class="d-flex">
        <HelpArea :items="helpItemsOverview"></HelpArea>
        <button
          v-if="hasUnmigratedChannels"
          class="btn btn-primary mr-2"
          @click="show = true"
        >
          <i class="fal" :class="migrationButtonIcon" />
          {{ migrationButtonText }}
        </button>
        <button
          v-if="store.getters.selectedApp?.name !== 'ebay'"
          class="btn btn-primary"
          @click="$router.push({ name: 'salesChannelManagementAppWizard' })"
        >
          <i class="fal fa-plus" />
          {{ $t("salesChannelManagementApp.addIntegration") }}
        </button>
        <ChatBotButton class="ml-2" />
      </div>
      <!--   END: LOGO/ADD INTEGRATION   -->
    </template>
    <MigrateIntegrationsModal v-if="show" />
    <SaveDialog
      :show-save-dialog="showSaveDialog"
      @close="showSaveDialog = false"
      @back="onSaveDialogBack"
      @save="onSave(true)"
    />
  </div>
</template>

<style lang="scss" scoped>
.shop-logo {
  max-width: 170px;
  width: 100%;
}

.alert {
  margin-bottom: 0;
}

i {
  color: var(--ax-fg-primary, #{$color-connect-text});
}

.product-selection-filter-menu {
  transform: translateX(
    12px
  ); // We have to move the menu a bit to the right, otherwise there is a small offset
}

@media screen and (max-width: 920px) {
  :deep(.form-helper) {
    .col-8 {
      display: none !important;
    }
  }
}
</style>
